import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import relativetime from 'dayjs/plugin/relativeTime';

import { WorklogType } from 'types/WorkLogTypes';

dayjs.extend(advancedFormat);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localeData);
dayjs.extend(duration);
dayjs.extend(relativetime);

/**
 * Converts a JS Date to a Unix timestamp in milliseconds via dayjs.
 */
export const convertDateToMills = (givenDate: Date) => {
  return dayjs(givenDate).valueOf();
};

/**
 * Returns a dayjs-formatted string where the time comes from value
 * but the date parts (year/month/day) come from date (or value if date is missing).
 * Useful for combining a date picker's date with a time picker's time.
 */
export const formatDate = (value: Dayjs | null, date?: Dayjs | null) => {
  return dayjs(value)
    .set('year', dayjs(date || value).get('year'))
    .set('month', dayjs(date || value).get('month'))
    .set('date', dayjs(date || value).get('date'))
    .format();
};

/**
 * Comparator for Array.sort() that orders worklogs newest → oldest,
 * using start_time or falling back to date.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SortWorkLogsByDescDate = (dataA: any, dataB: any) => {
  const dateA = dayjs(dataA?.start_time || dataA?.date);
  const dateB = dayjs(dataB?.start_time || dataB?.date);
  // Compare the 2 dates
  if (dateB < dateA) return -1;
  if (dateB > dateA) return 1;
  return 0;
};

/**
 * Same as above but oldest → newest.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SortWorkLogsByAscDate = (dataA: any, dataB: any) => {
  const dateA = dayjs(dataA?.start_time || dataA?.date);
  const dateB = dayjs(dataB?.start_time || dataB?.date);
  // Compare the 2 dates
  if (dateA < dateB) return -1;
  if (dateA > dateB) return 1;
  return 0;
};

/**
 * Returns total hours between startTime and endTime,
 * subtracting breakTime (in minutes). Returns 0 if either is null.
 */
export const CalculateTotalHours = (startTime: Dayjs | null, endTime: Dayjs | null, breakTime: number) => {
  if (startTime !== null && endTime !== null) {
    if (breakTime > 0) {
      const newEndTime = endTime.subtract(breakTime, 'minute');
      return newEndTime.diff(startTime, 'hour', true);
    } else {
      return endTime.diff(startTime, 'minute') / 60;
    }
  }

  return 0;
};

/**
 * Returns an array of 'YYYY-MM-DD' strings for every day between from and to (inclusive).
 * Returns [] if either is null.
 */
export const generateDateRange = (from: Dayjs | null, to: Dayjs | null) => {
  if (from === null || to === null) return [];

  // Array to store generated dates
  const generatedDates = [];

  // Iterate through the date range and generate dates from the range
  let currentDate = from;

  while (currentDate.startOf('day').isSameOrBefore(to.startOf('day'))) {
    generatedDates.push(currentDate.format('YYYY-MM-DD'));
    currentDate = currentDate.add(1, 'day');
  }

  return generatedDates;
};

/**
 * Generates the date range, then filters out weekends and any dates matching public holidays cached in localStorage under the 'public-holiday' key.
 * Note: it compares holidays by MM-DD only, so year is ignored — see the comment for the data.gov.au caveat about holiday data going stale past 2021.
 */
export const getBusinessDaysInRange = (fromDate: Dayjs | null, toDate: Dayjs | null) => {
  const selectedDateRange = generateDateRange(fromDate, toDate);
  const publicHolidayData = localStorage.getItem('public-holiday');
  const holidays = publicHolidayData ? JSON.parse(publicHolidayData) : [];

  // Our public holiday data will only return data for a specific year, or a range of years
  // In the case of range of years 2021-2025 is the current max. This will only return 2021 holidays as there has been no update to the data beyond 2021
  // We will format the data to only show month and day to compare with our selected date range
  // https://data.gov.au/dataset/ds-dga-b1bc6077-dadd-4f61-9f8c-002ab2cdff10/details
  const parsedHolidayData = holidays.map((holiday: string) => dayjs(holiday).format('MM-DD'));

  const totalDays = selectedDateRange?.filter(
    (date) =>
      dayjs(date).day() !== 0 && dayjs(date).day() !== 6 && !parsedHolidayData.includes(dayjs(date).format('MM-DD')),
  );

  return totalDays;
};

/**
 * Sums the worklog_hours field across an array of worklogs.
 * Returns 0 for an empty array.
 */
export const getTotalHoursBySpan = (worklogs: WorklogType[]) => {
  if (!worklogs.length) return 0;

  let totalHours = 0;

  worklogs.forEach((worklog) => {
    const hoursWorked = Number(worklog.worklog_hours);

    if (worklog.groupedIds?.length) {
      totalHours += worklog.groupedIds.length * hoursWorked;
    } else {
      totalHours += hoursWorked;
    }
  });

  return totalHours;
};

/**
 * Same idea as generateDateRange but takes non-nullable Dayjs inputs.
 * Slightly redundant with the above.
 */
export const getDatesBetween = (startDate: Dayjs, endDate: Dayjs): string[] => {
  const dates: string[] = [];
  let current = startDate;

  while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
    dates.push(current.format('YYYY-MM-DD'));
    current = current.add(1, 'day');
  }

  return dates;
};

/**
 * Converts an Excel cell value to a JS Date.
 * Handles three cases: already a Date (passes through), a number (Excel's serial date, anchored to 1899-12-30),
 * or a string (parsed via new Date()). Returns null for invalid input.
 */
export const excelDateToJS = (val: any): Date | null => {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof val === 'number') {
    const epoch = new Date(1899, 11, 30);
    return new Date(epoch.getTime() + val * 86400000);
  }
  if (typeof val === 'string') {
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/**
 * Formats a Date as DD/MM/YY (two-digit year).
 */
export const formatExcelDate = (d: Date | null): string => {
  if (!d) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
};

/**
 * Formats a Date in long Australian style, e.g. 27 May 2026.
 */
export const formatDateLong = (d: Date | null): string => {
  if (!d) return '';
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Returns a new Date set to the first day of the given date's month.
 */
export const startOfMonth = (d: Date): Date => {
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

/**
 * Returns a Date set to the 1st of the month n months from the given date.
 */
export const addMonths = (d: Date, n: number): Date => {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
};

/**
 * Returns the day difference between two Dates (whole days, not partial).
 */
export const getDuration = (startDate: Date, endDate: Date) => {
  if (!startDate || !endDate) return 0;

  return dayjs(endDate).diff(dayjs(startDate), 'days');
};

/**
 * Returns the break time in mins
 */
export const formatBreakTime = (breakTime: number) => {
  return `${Math.trunc(breakTime)} mins`;
};
